import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Users, Megaphone, Sparkles, Grid3x3,
  LogOut, ChevronRight, Building2, CreditCard, UsersRound, Activity, FileSpreadsheet, Shield, FlaskConical, BookOpen, Factory,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import TrialBanner from "@/components/TrialBanner";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import CommandPalette from "@/components/CommandPalette";
import { SupportButton } from "@/components/SupportButton";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/app/modules", icon: Grid3x3, label: "Modules" },
  { to: "/app/shopfloor", icon: Factory, label: "Shop Floor" },
  { to: "/app/ink", icon: FlaskConical, label: "Ink & Print" },
  { to: "/app/sops", icon: FileText, label: "SOPs" },
  { to: "/app/hr", icon: Users, label: "HR" },
  { to: "/app/marketing", icon: Megaphone, label: "Marketing" },
  { to: "/app/assistant", icon: Sparkles, label: "AI Assistant" },
  { to: "/app/templates", icon: FileSpreadsheet, label: "Templates" },
  { to: "/app/library", icon: BookOpen, label: "My Library" },
  { to: "/app/activity", icon: Activity, label: "Activity" },
  { to: "/app/team", icon: UsersRound, label: "Team" },
  { to: "/app/billing", icon: CreditCard, label: "Billing" },
];

export default function DashLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    api.get("/auth/is-super-admin")
      .then((r) => setIsSuperAdmin(!!r.data?.super_admin))
      .catch(() => setIsSuperAdmin(false));
  }, []);

  const fullNav = isSuperAdmin
    ? [...NAV, { to: "/app/admin", icon: Shield, label: "Admin" }]
    : NAV;

  const currentLabel = fullNav.find((n) => n.to === loc.pathname)?.label
    || (loc.pathname.startsWith("/app/sops") ? "SOPs" : "Workspace");

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-border flex flex-col bg-card">
        <div className="h-14 px-5 flex items-center border-b border-border">
          <div className="font-display font-black text-lg tracking-tight">
            Rule<span className="text-primary">AI</span>
            <span className="text-muted-foreground">.systems</span>
          </div>
        </div>
        <div className="px-3 pt-4 pb-2 overline text-muted-foreground">Workspace</div>
        <div className="px-3 mb-4">
          <div className="flex items-center gap-2 border border-border rounded-sm px-3 py-2">
            <Building2 className="w-4 h-4 text-primary" />
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="workspace-org-name">
                {user?.organization_name}
              </div>
              <div className="text-[10px] font-mono-plex uppercase tracking-wider text-muted-foreground">
                {user?.role}
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-2 space-y-0.5">
          {fullNav.map((n) => {
            const active = loc.pathname === n.to
              || (n.to !== "/app" && loc.pathname.startsWith(n.to));
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-secondary"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{n.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2 truncate">{user?.email}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start rounded-sm"
            data-testid="logout-button"
            onClick={() => { logout(); nav("/"); }}
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border px-8 flex items-center justify-between bg-card/70 backdrop-blur-xl">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-mono-plex uppercase tracking-widest text-xs">RuleAI</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground font-medium">{currentLabel}</span>
            </div>
            <CommandPalette />
          </div>
          <div className="flex items-center gap-3">
            <SupportButton />
            <ThemeToggle />
            <NotificationBell />
            <div className="text-xs font-mono-plex text-muted-foreground uppercase tracking-widest hidden lg:block">
              {user?.full_name}
            </div>
            <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground grid place-items-center text-xs font-bold">
              {user?.full_name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <TrialBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
